import React, { useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useMessage } from '@tmapy/intl';
import {
  CustomSelect,
  DangerAlert,
  Form,
  FormItem,
  FormRow,
  Input,
  SecondaryBtn,
  SvgPlus,
} from '@tmapy/style-guide';

import { FormBlock } from '../../app/components/FormBlock';
import { QUERY_STATE } from '../../constants';
import { useFormItemServerErrors, useSelect } from '../../hooks';
import type { ServerErrorItem } from '../../hooks/useFormItemServerErrors';
import type { ServerErrors } from '../../types';
import { usePriceListItemsListResource } from '../../openapi/operations/priceListItemsList';
import { usersList } from '../../openapi/operations/usersList';
import type { ServiceOrderEditRequest } from '../../openapi/types/ServiceOrderEdit';
import type { ServiceOrderListResponse } from '../../openapi/types/ServiceOrderList';
import type { UserListResponse } from '../../openapi/types/UserList';

import {
  getDocumentItemNameOverride,
  getUserLabel,
  isRowValid,
  splitItemsServerErrors,
  toItemRequest,
} from '../utils';
import type { ServiceOrderItemRowValue } from '../utils';

import { ServiceOrderItemRow } from './ServiceOrderItemRow';
import { ServiceOrderNoteField } from './ServiceOrderNoteField';
import {
  serviceOrderBtnMessages,
  serviceOrderFormMessages,
  serviceOrderMessages,
} from './serviceOrderMessages';

/** Nabídka je krátká, ale stránkování ji nesmí tiše useknout. */
const ITEMS_PAGE_SIZE = 200;

/** Uživatelů jsou tisíce, našeptávač jich ukáže jen první stránku k zadanému textu. */
const USERS_PAGE_SIZE = 20;

/** Na žádosti je strana `NestedUser`, z našeptávače `UserList` — společné je jen tohle. */
type PartyUser = {
  id: number;
  name?: string | null;
  surname?: string | null;
  company_name?: string | null;
};

/**
 * Serverová hláška má přednost před klientskou validací — server hlídá i pravidla,
 * na která formulář nevidí, a jeho odpověď se váže k naposledy odeslaným datům.
 */
const mergeFieldError = (server: ServerErrorItem | undefined, clientError: string | null) => ({
  isInvalid: !!server?.isInvalid || !!clientError,
  errorMessage: server?.errorMessage || clientError,
});

const renderUserSelectItem = (item: UserListResponse) => (
  <>
    {getUserLabel(item)}
    {item.ico && <span className='sg-u-c-body-darkest'>{` IČO ${item.ico}`}</span>}
  </>
);

/** Odesílací tlačítko je v přilepené hlavičce stránky, mimo `<form>`. */
export type ServiceOrderFormHandle = {
  submit(): void;
};

type ServiceOrderFormProps = {
  ref?: React.Ref<ServiceOrderFormHandle>;
  /** Rozpracovaná objednávka k editaci; `null` zakládá novou. */
  data: ServiceOrderListResponse | null;
  submitQueryState: QUERY_STATE;
  serverErrors?: ServerErrors | null;
  onSubmit(values: ServiceOrderEditRequest): void;
  onEdit?(): void;
};

export const ServiceOrderForm: React.FC<ServiceOrderFormProps> = ({
  ref,
  data,
  submitQueryState,
  serverErrors,
  onSubmit,
  onEdit,
}) => {
  const formatMessage = useMessage();

  const { items: rawItemsServerErrors, ...fieldServerErrors } = serverErrors ?? {};
  const itemsServerErrors = useMemo(
    () => splitItemsServerErrors(rawItemsServerErrors),
    [rawItemsServerErrors],
  );
  const formItemServerErrors = useFormItemServerErrors(
    itemsServerErrors.list.length
      ? { ...fieldServerErrors, items: itemsServerErrors.list }
      : fieldServerErrors,
  );

  const [validate, setValidate] = useState(false);
  const markEdited = useCallback(() => onEdit?.(), [onEdit]);

  const priceListItems = usePriceListItemsListResource({ page_size: ITEMS_PAGE_SIZE });

  const nextRowKey = useRef(0);
  const createRow = useCallback(
    (values: Partial<Omit<ServiceOrderItemRowValue, 'key'>> = {}): ServiceOrderItemRowValue => ({
      key: nextRowKey.current++,
      priceListItemId: '',
      quantity: '',
      isDataOrderLinked: false,
      dataOrder: null,
      documentItemName: '',
      ...values,
    }),
    [],
  );
  const [rows, setRows] = useState<ServiceOrderItemRowValue[]>(() =>
    data?.items.length
      ? data.items.map((item) =>
          createRow({
            priceListItemId: `${item.price_list_item.id}`,
            // U navázaného řádku je to plocha objednávky, po zrušení vazby ji jde upravit.
            quantity: item.quantity,
            isDataOrderLinked: item.data_order !== null,
            dataOrder: item.data_order !== null ? { id: item.data_order.id } : null,
            documentItemName: getDocumentItemNameOverride(item),
          }),
        )
      : [createRow()],
  );
  /** Server váže chyby řádků na pořadí v odeslaném `items`, ne na aktuální stav formuláře. */
  const [submittedRowKeys, setSubmittedRowKeys] = useState<number[]>([]);

  const [name, setName] = useState(data?.name ?? '');
  const [note, setNote] = useState(data?.note ?? '');

  const isSaving = submitQueryState === QUERY_STATE.WAITING;

  const prepareUserItems = useCallback(async (search: string) => {
    const response = await usersList({
      query: { ...(search ? { search } : {}), is_active: true, page_size: USERS_PAGE_SIZE },
    });
    if (!response.ok) return null;
    return response.data?.results;
  }, []);

  const handleRowChange = useCallback(
    (changed: ServiceOrderItemRowValue) => {
      markEdited();
      setRows((current) => current.map((row) => (row.key === changed.key ? changed : row)));
    },
    [markEdited],
  );

  const handleRowRemove = useCallback(
    (key: number) => {
      markEdited();
      setRows((current) => current.filter((row) => row.key !== key));
    },
    [markEdited],
  );

  const handleRowAdd = useCallback(() => {
    markEdited();
    setRows((current) => [...current, createRow()]);
  }, [markEdited, createRow]);

  const zadatel = useSelect<PartyUser | null, UserListResponse>({
    name: 'zadatel',
    label: formatMessage(serviceOrderMessages.zadatel),
    initialValue: data?.zadatel ?? null,
    onChange: markEdited,
    prepareItems: prepareUserItems,
    renderItem: renderUserSelectItem,
    renderValue: getUserLabel,
  });

  const odberatel = useSelect<PartyUser | null, UserListResponse>({
    name: 'odberatel',
    label: formatMessage(serviceOrderMessages.odberatel),
    initialValue: data?.odberatel ?? null,
    onChange: markEdited,
    prepareItems: prepareUserItems,
    renderItem: renderUserSelectItem,
    renderValue: getUserLabel,
  });

  const areRowsValid = rows.every(isRowValid);
  /**
   * Název doplní server jen při založení. Uložení draftu je PATCH, kde by vynechaný
   * název znamenal „neměň“ a smazaný text by se po uložení tiše vrátil.
   */
  const isNameRequired = !!data;
  const nameClientError =
    isNameRequired && !name.trim() ? formatMessage(serviceOrderFormMessages.nameError) : null;
  const odberatelClientError = odberatel.value
    ? null
    : formatMessage(serviceOrderFormMessages.userError);

  const handleNameChange = useCallback(
    (_e: React.SyntheticEvent, eventData?: { value: string }) => {
      markEdited();
      setName(eventData?.value ?? '');
    },
    [markEdited],
  );

  const handleNoteChange = useCallback(
    (value: string) => {
      markEdited();
      setNote(value);
    },
    [markEdited],
  );

  const handleSubmit = useCallback(() => {
    setValidate(true);
    if (!areRowsValid || nameClientError || !odberatel.value) return;

    setSubmittedRowKeys(rows.map((row) => row.key));
    const trimmedName = name.trim();
    onSubmit({
      ...(trimmedName ? { name: trimmedName } : {}),
      note: note.trim(),
      items: rows.map(toItemRequest),
      // Vynechaný klíč by PATCH bral jako „neměň“, odebrat žadatele jde jen explicitním null.
      zadatel: zadatel.value?.id ?? null,
      odberatel: odberatel.value.id,
    });
  }, [areRowsValid, nameClientError, rows, name, note, zadatel.value, odberatel.value, onSubmit]);

  useImperativeHandle(ref, () => ({ submit: handleSubmit }), [handleSubmit]);

  const nameError = mergeFieldError(formItemServerErrors.name, validate ? nameClientError : null);
  const zadatelError = mergeFieldError(formItemServerErrors.zadatel, null);
  const odberatelError = mergeFieldError(
    formItemServerErrors.odberatel,
    validate ? odberatelClientError : null,
  );

  return (
    <Form onSubmit={handleSubmit}>
      <div className='sg-u-vs-4'>
        <section>
          <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
            <FormattedMessage {...serviceOrderFormMessages.detailsTitle} />
          </h3>
          <FormBlock>
            <FormRow>
              <FormItem
                label={formatMessage(serviceOrderMessages.name)}
                isRequired={isNameRequired}
                {...nameError}
              >
                <Input
                  name='name'
                  value={name}
                  isDisabled={isSaving}
                  isInvalid={nameError.isInvalid}
                  onChange={handleNameChange}
                />
                <div className='sg-a-mt-1/2 sg-u-type-sml sg-u-c-body-darkest'>
                  <FormattedMessage
                    {...(isNameRequired
                      ? serviceOrderFormMessages.nameEditHint
                      : serviceOrderFormMessages.nameHint)}
                  />
                </div>
              </FormItem>
            </FormRow>
            <FormRow>
              <FormItem {...odberatel} isRequired {...odberatelError}>
                <CustomSelect
                  {...odberatel}
                  inputPlaceholder={formatMessage(serviceOrderFormMessages.userPlaceholder)}
                  isDisabled={isSaving}
                  isInvalid={odberatelError.isInvalid}
                />
              </FormItem>
              <FormItem {...zadatel} {...zadatelError}>
                <CustomSelect
                  {...zadatel}
                  inputPlaceholder={formatMessage(serviceOrderFormMessages.userPlaceholder)}
                  isDisabled={isSaving}
                  isInvalid={zadatelError.isInvalid}
                  hasClearBtn
                />
              </FormItem>
            </FormRow>
            <FormRow>
              <ServiceOrderNoteField
                value={note}
                isDisabled={isSaving}
                serverError={formItemServerErrors.note}
                onChange={handleNoteChange}
              />
            </FormRow>
          </FormBlock>
        </section>
        <section>
          <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
            <FormattedMessage {...serviceOrderMessages.items} />
          </h3>
          <FormBlock>
            {rows.map((row, index) => (
              <div key={row.key} className={index > 0 ? 'sg-u-bt sg-a-pt-2' : undefined}>
                <ServiceOrderItemRow
                  row={row}
                  priceListItems={priceListItems.data?.results ?? []}
                  serverErrors={itemsServerErrors.rows[submittedRowKeys.indexOf(row.key)] ?? null}
                  validate={validate}
                  isDisabled={isSaving}
                  canRemove={rows.length > 1}
                  onChange={handleRowChange}
                  onRemove={handleRowRemove}
                />
              </div>
            ))}
            <div>
              <SecondaryBtn
                icon={{ position: 'left', element: <SvgPlus /> }}
                isDisabled={isSaving}
                onClick={handleRowAdd}
              >
                <FormattedMessage {...serviceOrderBtnMessages.addItem} />
              </SecondaryBtn>
            </div>
            {formItemServerErrors.items?.errorMessage && (
              <DangerAlert>{formItemServerErrors.items.errorMessage}</DangerAlert>
            )}
            {priceListItems.queryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...serviceOrderFormMessages.priceListLoadFail} />
              </DangerAlert>
            )}
          </FormBlock>
        </section>
      </div>
    </Form>
  );
};
